/**
 * Route-level lazy loader with chunk-miss recovery.
 *
 * A deploy rotates hashed chunk filenames. A tab that was opened before the
 * deploy (or an installed PWA holding a stale service-worker cache) then asks
 * for a URL that no longer exists and `import()` rejects with
 * "Failed to fetch dynamically imported module" — React's Suspense boundary
 * turns that into a blank page, which production telemetry showed on / and
 * /auth.
 *
 * Recovery ladder:
 *   1. retry the import once after a short delay (transient mobile network,
 *      cache revalidation)
 *   2. hard reload once, so the browser picks up the new HTML and chunk names
 *   3. if we already reloaded in this tab, rethrow so the error boundary shows
 *      a real message instead of looping
 *
 * `clearChunkReloadGuard()` is called once the app renders successfully, so a
 * later, unrelated deploy is allowed its own single reload.
 */
import { lazy, type ComponentType } from "react";
import { isPreviewEnv } from "@/lib/isPreviewEnv";

const RELOAD_FLAG = "leadxkids:chunk_reload";

function alreadyReloaded(): boolean {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG) === "1";
  } catch {
    return false;
  }
}

function markReloaded(): void {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch {
    /* private mode */
  }
}

export function clearChunkReloadGuard(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* private mode */
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
// Pages take their own props; `any` keeps the wrapper transparent to callers.
type PageModule = { default: ComponentType<any> };

export function lazyPage<T extends PageModule>(load: () => Promise<T>) {
  // One shared in-flight promise, so hover-prefetch and the actual render never
  // download the same chunk twice.
  let started: Promise<T> | null = null;
  const loadOnce = () => (started ??= load());

  const Component = lazy(async () => {
    try {
      return await loadOnce();
    } catch {
      started = null;
      await new Promise((resolve) => setTimeout(resolve, 300));
      try {
        return await loadOnce();
      } catch (second) {
        started = null;
        const canUseProductionRecovery =
          typeof window !== "undefined" && !import.meta.env.DEV && !isPreviewEnv();
        if (canUseProductionRecovery && !alreadyReloaded()) {
          markReloaded();
          window.location.reload();
          // Render nothing while the reload is in flight; throwing here would
          // flash the error boundary on the way out.
          return { default: () => null } as unknown as T;
        }
        throw second;
      }
    }
  }) as ReturnType<typeof lazy<(props: any) => any>> & { preload: () => Promise<void> };

  // Warm the chunk before the user commits to the navigation (hover / touch
  // start), so the route renders without hitting the Suspense fallback.
  Component.preload = async () => {
    try {
      await loadOnce();
    } catch {
      started = null;
    }
  };

  return Component;
}
